import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
    X,
    Check,
    CheckCheck,
    Trash2,
    Share2,
    Info,
    AlertTriangle,
    AlertCircle,
    CheckCircle,
    ExternalLink,
    Clock,
    Bell,
    Filter,
    Inbox,
    Archive
} from 'lucide-react';
import '../styles/NotificationInboxFull.css';

const NotificationInboxFull = ({ isOpen, onClose, fileApiRequest, onNavigate }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all'); // all, unread, read

    // Fetch notifications
    const fetchNotifications = async () => {
        try {
            setLoading(true);
            let url = '/notifications?limit=100';

            if (filter === 'unread') {
                url += '&is_read=false';
            } else if (filter === 'read') {
                url += '&is_read=true';
            }

            const response = await fileApiRequest(url);
            setNotifications(response?.notifications || []);
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
        } finally {
            setLoading(false);
        }
    };

    // Mark notification as read
    const markAsRead = async (notificationId) => {
        try {
            await fileApiRequest(`/notifications/${notificationId}`, {
                method: 'PATCH',
                body: JSON.stringify({ is_read: true })
            });
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)
            );
        } catch (err) {
            console.error('Failed to mark as read:', err);
        }
    };

    // Dismiss notification
    const dismissNotification = async (notificationId) => {
        try {
            await fileApiRequest(`/notifications/${notificationId}`, {
                method: 'PATCH',
                body: JSON.stringify({ is_dismissed: true })
            });
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
        } catch (err) {
            console.error('Failed to dismiss notification:', err);
        }
    };

    // Mark all as read
    const markAllAsRead = async () => {
        try {
            await fileApiRequest('/notifications/mark-all-read', { method: 'POST' });
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() })));
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    };

    // Dismiss all
    const dismissAll = async () => {
        try {
            await fileApiRequest('/notifications/dismiss-all', { method: 'POST' });
            setNotifications([]);
        } catch (err) {
            console.error('Failed to dismiss all:', err);
        }
    };

    // Handle notification click
    const handleNotificationClick = (notification) => {
        if (!notification.is_read) {
            markAsRead(notification.id);
        }

        if (notification.action_url && onNavigate) {
            onNavigate(notification.action_url);
            onClose();
        }
    };

    // Get icon based on notification type/category
    const getNotificationIcon = (notification) => {
        const { type, category } = notification;

        if (category === 'file_share') {
            return <Share2 className="notif-icon-full share" />;
        }

        switch (type) {
            case 'success':
                return <CheckCircle className="notif-icon-full success" />;
            case 'warning':
                return <AlertTriangle className="notif-icon-full warning" />;
            case 'error':
                return <AlertCircle className="notif-icon-full error" />;
            default:
                return <Info className="notif-icon-full info" />;
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    // Format relative time
    const formatRelativeTime = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

        return formatDate(dateString);
    };

    // Fetch on open and filter change
    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen, filter]);

    // Close on escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const unreadCount = notifications.filter(n => !n.is_read).length;

    // Use portal to render at document body level for proper z-index stacking
    return ReactDOM.createPortal(
        <div className="notification-inbox-full-overlay" onClick={onClose}>
            <div className="notification-inbox-full-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="nif-header">
                    <div className="nif-header-left">
                        <Inbox className="nif-header-icon" />
                        <div>
                            <h2>Notifications</h2>
                            <span className="nif-subtitle">
                                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                            </span>
                        </div>
                    </div>
                    <button className="nif-close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Toolbar */}
                <div className="nif-toolbar">
                    <div className="nif-filters">
                        <div className="nif-filter-group">
                            <button
                                className={`nif-filter-btn ${filter === 'all' ? 'active' : ''}`}
                                onClick={() => setFilter('all')}
                            >
                                All
                            </button>
                            <button
                                className={`nif-filter-btn ${filter === 'unread' ? 'active' : ''}`}
                                onClick={() => setFilter('unread')}
                            >
                                Unread
                            </button>
                            <button
                                className={`nif-filter-btn ${filter === 'read' ? 'active' : ''}`}
                                onClick={() => setFilter('read')}
                            >
                                Read
                            </button>
                        </div>
                    </div>
                    <div className="nif-actions">
                        {unreadCount > 0 && (
                            <button className="nif-action-btn" onClick={markAllAsRead}>
                                <CheckCheck size={16} />
                                Mark all read
                            </button>
                        )}
                        {notifications.length > 0 && (
                            <button className="nif-action-btn danger" onClick={dismissAll}>
                                <Trash2 size={16} />
                                Clear all
                            </button>
                        )}
                    </div>
                </div>

                {/* Notification List */}
                <div className="nif-list">
                    {loading ? (
                        <div className="nif-loading">
                            <div className="nif-spinner"></div>
                            <span>Loading notifications...</span>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="nif-empty">
                            <Bell className="nif-empty-icon" />
                            <h3>No notifications</h3>
                            <p>
                                {filter === 'unread'
                                    ? "You've read all your notifications"
                                    : filter === 'read'
                                    ? "No read notifications"
                                    : "You're all caught up!"}
                            </p>
                        </div>
                    ) : (
                        notifications.map(notification => (
                            <div
                                key={notification.id}
                                className={`nif-item ${!notification.is_read ? 'unread' : ''}`}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <div className="nif-item-icon">
                                    {getNotificationIcon(notification)}
                                </div>
                                <div className="nif-item-content">
                                    <div className="nif-item-header">
                                        <span className="nif-item-title">{notification.title}</span>
                                        <div className="nif-item-badges">
                                            {!notification.is_read && (
                                                <span className="nif-unread-badge">New</span>
                                            )}
                                        </div>
                                    </div>
                                    <p className="nif-item-message">{notification.message}</p>
                                    <div className="nif-item-footer">
                                        <span className="nif-item-time">
                                            <Clock size={12} />
                                            {formatRelativeTime(notification.created_at)}
                                        </span>
                                        {notification.action_text && (
                                            <button className="nif-item-action">
                                                {notification.action_text}
                                                <ExternalLink size={12} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="nif-item-actions">
                                    {!notification.is_read && (
                                        <button
                                            className="nif-item-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                markAsRead(notification.id);
                                            }}
                                            title="Mark as read"
                                        >
                                            <Check size={16} />
                                        </button>
                                    )}
                                    {notification.is_dismissable && (
                                        <button
                                            className="nif-item-btn danger"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dismissNotification(notification.id);
                                            }}
                                            title="Dismiss"
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default NotificationInboxFull;
